// Analytics Service for NLFX Cable Builder
// Comprehensive tracking and reporting system

class AnalyticsService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.userId = this.getUserId();
    this.startTime = Date.now();
    this.events = [];
    this.metrics = {
      pageViews: 0,
      cableConfigurations: 0,
      cartAdditions: 0,
      completions: 0,
      errors: 0,
      timeOnSite: 0
    };
    this.userJourney = [];
    this.performanceMetrics = {};
    
    this.init();
  }

  init() {
    // Initialize analytics
    this.trackPageView();
    this.setupEventListeners();
    this.startPerformanceMonitoring();
    this.startSessionTimer();
    
    // Send initial session data
    this.sendAnalytics('session_start', {
      sessionId: this.sessionId,
      userId: this.userId,
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language
    });
  }

  // Generate unique session ID
  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get or create user ID
  getUserId() {
    let userId = localStorage.getItem('analytics_user_id');
    if (!userId) {
      userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('analytics_user_id', userId);
    }
    return userId;
  }

  // Setup event listeners for automatic tracking
  setupEventListeners() {
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackEvent('page_hidden');
      } else {
        this.trackEvent('page_visible');
      }
    });

    // Track before unload
    window.addEventListener('beforeunload', () => {
      this.trackEvent('page_exit', {
        timeOnSite: Date.now() - this.startTime
      });
      this.sendAnalytics('session_end', {
        sessionId: this.sessionId,
        timeOnSite: Date.now() - this.startTime,
        events: this.events.length
      });
    });

    // Track errors
    window.addEventListener('error', (event) => {
      this.trackError('javascript_error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });

    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError('promise_rejection', {
        reason: event.reason
      });
    });
  }

  // Start performance monitoring
  startPerformanceMonitoring() {
    if ('performance' in window) {
      // Monitor page load performance
      window.addEventListener('load', () => {
        const perfData = performance.getEntriesByType('navigation')[0];
        this.performanceMetrics = {
          domContentLoaded: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
          loadComplete: perfData.loadEventEnd - perfData.loadEventStart,
          firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime,
          firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime
        };
        
        this.trackEvent('performance_metrics', this.performanceMetrics);
      });
    }
  }

  // Start session timer
  startSessionTimer() {
    setInterval(() => {
      this.metrics.timeOnSite = Date.now() - this.startTime;
    }, 30000); // Update every 30 seconds
  }

  // Track page view
  trackPageView(page = window.location.pathname) {
    this.metrics.pageViews++;
    this.trackEvent('page_view', {
      page,
      referrer: document.referrer,
      title: document.title
    });
  }

  // Track user event
  trackEvent(eventName, data = {}) {
    const event = {
      eventName,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userId,
      data
    };

    this.events.push(event);
    this.userJourney.push({
      action: eventName,
      timestamp: event.timestamp,
      data
    });

    // Send to analytics endpoint
    this.sendAnalytics('event', event);

    // Update metrics based on event type
    this.updateMetrics(eventName, data);
  }

  // Track cable configuration
  trackCableConfiguration(config, step) {
    this.metrics.cableConfigurations++;
    
    this.trackEvent('cable_configuration', {
      step,
      currentStep: step,
      configType: config.type,
      configStyle: config.style,
      connectors: {
        endA: config.connectors?.endA?.connector,
        endB: config.connectors?.endB?.connector
      },
      length: config.length,
      wireGauge: config.wireGauge?.gauge,
      finishing: config.finishing
    });
  }

  // Track cart addition
  trackCartAddition(config, price) {
    this.metrics.cartAdditions++;
    
    // Generate SKU for tracking
    const sku = this.generateSKU(config);
    
    this.trackEvent('cart_addition', {
      configType: config.type,
      price,
      sku,
      connectors: {
        endA: config.connectors?.endA?.connector,
        endB: config.connectors?.endB?.connector
      },
      length: config.length,
      wireGauge: config.wireGauge?.gauge,
      finishing: config.finishing
    });
  }

  // Generate SKU from config
  generateSKU(config) {
    const { type, connectors, length, wireGauge } = config;
    const endA = connectors?.endA?.connector || 'UNK';
    const endB = connectors?.endB?.connector || 'UNK';
    const gauge = wireGauge?.gauge || 'UNK';
    const len = length || 'UNK';
    
    return `${type?.toUpperCase()}-${endA}-${endB}-${gauge}-${len}`.replace(/\s+/g, '');
  }

  // Track completion
  trackCompletion(config, totalPrice) {
    this.metrics.completions++;
    
    this.trackEvent('cable_completion', {
      configType: config.type,
      totalPrice,
      timeToComplete: Date.now() - this.startTime,
      stepsCompleted: this.userJourney.filter(j => j.action.includes('cable_configuration')).length
    });
  }

  // Track error
  trackError(errorType, errorData) {
    this.metrics.errors++;
    
    this.trackEvent('error', {
      errorType,
      ...errorData,
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  }

  // Track user interaction
  trackInteraction(element, action, data = {}) {
    this.trackEvent('interaction', {
      element,
      action,
      ...data
    });
  }

  // Track search
  trackSearch(query, results) {
    this.trackEvent('search', {
      query,
      resultsCount: results.length,
      timestamp: new Date().toISOString()
    });
  }

  // Track pricing calculation
  trackPricingCalculation(config, price, breakdown) {
    this.trackEvent('pricing_calculation', {
      configType: config.type,
      price,
      breakdown,
      timestamp: new Date().toISOString()
    });
  }

  // Track compatibility check
  trackCompatibilityCheck(connectorA, connectorB, isCompatible) {
    this.trackEvent('compatibility_check', {
      connectorA,
      connectorB,
      isCompatible,
      timestamp: new Date().toISOString()
    });
  }

  // Track mobile app events
  trackMobileEvent(eventName, data = {}) {
    this.trackEvent(`mobile_${eventName}`, {
      ...data,
      isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      isPWA: window.matchMedia('(display-mode: standalone)').matches
    });
  }

  // Track accessibility usage
  trackAccessibilityUsage(feature, enabled) {
    this.trackEvent('accessibility_usage', {
      feature,
      enabled,
      timestamp: new Date().toISOString()
    });
  }

  // Update metrics based on event
  updateMetrics(eventName, data) {
    switch (eventName) {
      case 'cable_configuration':
        this.metrics.cableConfigurations++;
        break;
      case 'cart_addition':
        this.metrics.cartAdditions++;
        break;
      case 'cable_completion':
        this.metrics.completions++;
        break;
      case 'error':
        this.metrics.errors++;
        break;
    }
  }

  // Send analytics data to server
  async sendAnalytics(type, data) {
    try {
      const payload = {
        type,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        userId: this.userId,
        data
      };

      // Send to analytics endpoint
      const response = await fetch('/api/analytics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        console.warn('Analytics data not sent:', response.status);
      }
    } catch (error) {
      console.warn('Failed to send analytics:', error);
      // Store locally for later sync
      this.storeOfflineAnalytics(type, data);
    }
  }

  // Store analytics data offline
  storeOfflineAnalytics(type, data) {
    const offlineData = JSON.parse(localStorage.getItem('offline_analytics') || '[]');
    offlineData.push({
      type,
      data,
      timestamp: new Date().toISOString()
    });
    
    // Keep only last 100 offline events
    if (offlineData.length > 100) {
      offlineData.splice(0, offlineData.length - 100);
    }
    
    localStorage.setItem('offline_analytics', JSON.stringify(offlineData));
  }

  // Sync offline analytics
  async syncOfflineAnalytics() {
    const offlineData = JSON.parse(localStorage.getItem('offline_analytics') || '[]');
    
    if (offlineData.length === 0) return;

    try {
      const response = await fetch('/api/analytics/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: this.sessionId,
          userId: this.userId,
          events: offlineData
        })
      });

      if (response.ok) {
        localStorage.removeItem('offline_analytics');
      }
    } catch (error) {
      console.warn('Failed to sync offline analytics:', error);
    }
  }

  // Get current metrics
  getMetrics() {
    return {
      ...this.metrics,
      sessionId: this.sessionId,
      userId: this.userId,
      timeOnSite: Date.now() - this.startTime,
      eventsCount: this.events.length
    };
  }

  // Get user journey
  getUserJourney() {
    return this.userJourney;
  }

  // Get performance metrics
  getPerformanceMetrics() {
    return this.performanceMetrics;
  }

  // Generate session report
  generateSessionReport() {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
      startTime: new Date(this.startTime).toISOString(),
      endTime: new Date().toISOString(),
      duration: Date.now() - this.startTime,
      metrics: this.metrics,
      userJourney: this.userJourney,
      performanceMetrics: this.performanceMetrics,
      events: this.events
    };
  }

  // Track conversion funnel
  trackFunnelStep(step, data = {}) {
    this.trackEvent('funnel_step', {
      step,
      stepNumber: this.getFunnelStepNumber(step),
      ...data
    });
  }

  // Get funnel step number
  getFunnelStepNumber(step) {
    const funnelSteps = [
      'page_view',
      'cable_type_selection',
      'style_selection', 
      'connector_selection',
      'length_selection',
      'finishing_selection',
      'review',
      'cart_addition',
      'completion'
    ];
    
    return funnelSteps.indexOf(step) + 1;
  }

  // Track A/B test
  trackABTest(testName, variant, data = {}) {
    this.trackEvent('ab_test', {
      testName,
      variant,
      ...data
    });
  }

  // Track heatmap data
  trackHeatmapData(element, x, y, action) {
    this.trackEvent('heatmap_interaction', {
      element,
      coordinates: { x, y },
      action,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });
  }

  // Track scroll depth
  trackScrollDepth(depth) {
    this.trackEvent('scroll_depth', {
      depth,
      page: window.location.pathname
    });
  }

  // Track time on page
  trackTimeOnPage(page, timeSpent) {
    this.trackEvent('time_on_page', {
      page,
      timeSpent
    });
  }

  // Track form interactions
  trackFormInteraction(formName, field, action, value = null) {
    this.trackEvent('form_interaction', {
      formName,
      field,
      action,
      value: value ? value.toString().substring(0, 100) : null // Limit value length
    });
  }

  // Track API calls
  trackAPICall(endpoint, method, status, duration) {
    this.trackEvent('api_call', {
      endpoint,
      method,
      status,
      duration
    });
  }

  // Track resource loading
  trackResourceLoad(resource, type, duration, success) {
    this.trackEvent('resource_load', {
      resource,
      type,
      duration,
      success
    });
  }

  // Get analytics summary
  getAnalyticsSummary() {
    const now = Date.now();
    const sessionDuration = now - this.startTime;
    
    return {
      session: {
        id: this.sessionId,
        userId: this.userId,
        duration: sessionDuration,
        startTime: new Date(this.startTime).toISOString()
      },
      metrics: {
        ...this.metrics,
        timeOnSite: sessionDuration,
        conversionRate: this.metrics.completions / Math.max(this.metrics.cableConfigurations, 1),
        errorRate: this.metrics.errors / Math.max(this.events.length, 1)
      },
      performance: this.performanceMetrics,
      journey: {
        steps: this.userJourney.length,
        lastAction: this.userJourney[this.userJourney.length - 1]?.action
      }
    };
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService; 